/**
 * Preparação do HTML do backend para ser exibido no iframe sandboxed.
 *
 * O front acrescenta duas coisas ao que o backend mandou, de forma que o backend possa se limitar
 * a HTML cru: uma folha de estilo base (injetada antes, para que qualquer `<style>` do backend
 * ainda tenha a última palavra) e um script-ponte que reporta cliques, submits e altura ao front.
 */

export const DEBUG_MESSAGE_SOURCE = 'diel-debug-page';

export type DebugPageMessage =
  | { source: typeof DEBUG_MESSAGE_SOURCE, type: 'navigate', href: string, newTab: boolean }
  | { source: typeof DEBUG_MESSAGE_SOURCE, type: 'height', height: number };

export function isDebugPageMessage(data: unknown): data is DebugPageMessage {
  if (typeof data !== 'object' || data === null) return false;
  const message = data as Partial<DebugPageMessage>;
  if (message.source !== DEBUG_MESSAGE_SOURCE) return false;
  return message.type === 'navigate' || message.type === 'height';
}

const BASE_STYLE = `
  :root { color-scheme: light; }
  body {
    margin: 0;
    padding: 16px;
    background: #fff;
    color: #464555;
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    line-height: 1.5;
  }
  h1, h2, h3 { color: #181842; margin: 0 0 12px; line-height: 1.25; }
  h1 { font-size: 20px; }
  h2 { font-size: 16px; margin-top: 24px; }
  h3 { font-size: 14px; margin-top: 20px; }
  a { color: #363BC4; }
  p { margin: 0 0 12px; }
  code, pre, .mono { font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', monospace; font-size: 12px; }
  pre {
    background: #F8F8F8;
    border: 1px solid #E9E7E9;
    border-radius: 6px;
    padding: 12px;
    overflow-x: auto;
  }
  table { border-collapse: collapse; width: 100%; margin: 0 0 16px; }
  th, td { border: 1px solid #E9E7E9; padding: 6px 10px; text-align: left; vertical-align: top; }
  th { background: #F8F8F8; color: #181842; font-weight: 600; white-space: nowrap; }
  tr:nth-child(even) td { background: #FCFCFC; }
  button, input, select { font: inherit; padding: 4px 8px; border: 1px solid #CDD0D5; border-radius: 5px; }
  button { background: #363BC4; border-color: #363BC4; color: #fff; cursor: pointer; }
  hr { border: none; border-top: 1px solid #E9E7E9; margin: 20px 0; }
`;

/**
 * Roda dentro do iframe. Sem `allow-same-origin`, então não alcança nem o localStorage nem o DOM
 * do app — a única via de comunicação é o `postMessage` abaixo, que só carrega href e altura.
 */
const BRIDGE_SCRIPT = `
(function () {
  var post = function (payload) {
    payload.source = '${DEBUG_MESSAGE_SOURCE}';
    parent.postMessage(payload, '*');
  };

  // Toda navegação é mediada pelo front: nada sai do iframe por conta própria, o que permite
  // manter o sandbox sem 'allow-popups' e ainda respeitar target="_blank" e ctrl/cmd+clique.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    var anchor = event.target && event.target.closest && event.target.closest('a[href]');
    if (!anchor) return;
    event.preventDefault();
    var newTab = anchor.target === '_blank' || event.metaKey || event.ctrlKey || event.shiftKey;
    post({ type: 'navigate', href: anchor.getAttribute('href'), newTab: newTab });
  });

  document.addEventListener('submit', function (event) {
    event.preventDefault();
    var form = event.target;
    var action = (form.getAttribute('action') || '').split('?')[0];
    var query = new URLSearchParams(new FormData(form)).toString();
    post({ type: 'navigate', href: action + (query ? '?' + query : ''), newTab: false });
  });

  var lastHeight = 0;
  var reportHeight = function () {
    var height = Math.ceil(document.documentElement.scrollHeight);
    if (height === lastHeight) return;
    lastHeight = height;
    post({ type: 'height', height: height });
  };
  window.addEventListener('load', reportHeight);
  if (window.ResizeObserver) new ResizeObserver(reportHeight).observe(document.documentElement);
  reportHeight();
})();
`;

function insertAfterOpeningTag(html: string, tagName: string, content: string): string|null {
  const match = new RegExp(`<${tagName}\\b[^>]*>`, 'i').exec(html);
  if (!match) return null;
  const index = match.index + match[0].length;
  return html.slice(0, index) + content + html.slice(index);
}

function insertBeforeClosingTag(html: string, closingTag: string, content: string): string|null {
  const index = html.toLowerCase().lastIndexOf(closingTag);
  if (index === -1) return null;
  return html.slice(0, index) + content + html.slice(index);
}

export function buildDebugDocument(html: string): string {
  const styleTag = `<style>${BASE_STYLE}</style>`;
  const scriptTag = `<script>${BRIDGE_SCRIPT}</script>`;

  // O backend pode mandar um documento completo ou apenas um fragmento; os dois casos funcionam.
  // O estilo base entra no início do head — e não antes do `</head>` — para que qualquer `<style>`
  // do backend venha depois e continue vencendo por ordem de cascata.
  const withStyle = insertAfterOpeningTag(html, 'head', styleTag)
    ?? insertAfterOpeningTag(html, 'html', styleTag)
    ?? styleTag + html;

  return insertBeforeClosingTag(withStyle, '</body>', scriptTag) ?? withStyle + scriptTag;
}
